import { createContext, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'

import { apiKey } from '~/configs/app'
import { SUCCESS_MESSAGES } from '~/constants/messages'
import useDebugger from '~/hooks/useDebugger'
import useQueueSound from '~/hooks/useQueueSound'
import useSocket from '~/hooks/useSocket'
import { TicketData } from '~/types/call'
import type { OptionProps } from '~/types/component'
import type { LayoutProps } from '~/types/layout'
import type { PreviewContextProps, SocketResGetQueue, SocketResUpdateQueue } from '~/types/preview'

const defaultTicket: TicketData = {
  queue_id: '',
  queue_no: '',
  queue_secret_code: '',
  counter_run_id: '',
  counter_lane: '',
  room: '',
  created_at: '',
  trigger: 'unset',
}

const PreviewContext = createContext<PreviewContextProps>({
  isPreviewStarted: false,
  isLoading: false,
  queueList: { A0: '' },
  startPreview: () => {},
  calledTicket: defaultTicket,
  locationName: '',
})

const PreviewProvider = ({ children }: LayoutProps) => {
  const socket = useSocket()
  const { debug } = useDebugger()
  const { playQueueSound } = useQueueSound()

  const [isPreviewStarted, setIsPreviewStarted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSound, setIsSound] = useState<boolean>(false)
  const [queueList, setQueueList] = useState<{ [key: string]: string }>({ A0: '' })
  const [calledTicket, setCalledTicket] = useState<TicketData>(defaultTicket)
  const [queueCalledTicket, setQueueCalledTicket] = useState<TicketData[]>([])
  const [isCalledTicketRunning, setIsCalledTicketRunning] = useState<boolean>(false)
  const [locationName, setLocationName] = useState<string>('')

  const headers = {
    headers: {
      'X-API-KEY': apiKey,
    },
  }

  const user_type = 'preview'

  const startPreview = (branch: OptionProps, room: string, sound: boolean) => {
    setIsLoading(true)

    const location_id = branch.value
    setLocationName(branch.label)

    if (sound) {
      setIsSound(sound)
    }

    if (socket) {
      socket.emit(
        'join-preview',
        {
          room,
          location_id,
          user_type,
          headers,
        },
        (response: SocketResGetQueue) => {
          const { status, msg, info } = response

          if (status) {
            setQueueList(info)

            toast.success(SUCCESS_MESSAGES.SUCCESS_START_PREVIEW)
            setIsPreviewStarted(true)
          } else {
            toast.error(msg)
          }

          setIsLoading(false)

          debug('info', 'Socket `join-preview` response:', response)
        },
      )
    }
  }

  const handleCsUpdate = useCallback(
    (response: SocketResUpdateQueue) => {
      const { info: ticket } = response

      if (ticket.trigger === 'next' || ticket.trigger === 'moveback' || ticket.trigger === 'recall') {
        if (isCalledTicketRunning || queueCalledTicket.length > 0) {
          setQueueCalledTicket((prevQueue) => [...prevQueue, ticket])
        } else {
          setIsCalledTicketRunning(true)
          setTimeout(() => {
            setIsCalledTicketRunning(false)
          }, 10000)

          setCalledTicket(ticket)
          if (isSound && ticket.queue_no && ticket.counter_lane) {
            playQueueSound(ticket.queue_no, ticket.counter_lane)
              .then(() => debug('info', 'Queue sound played'))
              .catch(() => debug('error', 'Queue sound error'))
          }
        }
      }

      if (ticket.trigger === 'serve') {
        if (ticket.queue_no === calledTicket.queue_no) {
          setCalledTicket(defaultTicket)
        }
        setQueueList((prevQueueList) => ({
          ...prevQueueList,
          [ticket.counter_lane as string]: ticket.queue_no,
        }))
      }

      if (ticket.trigger === 'finish') {
        setQueueList((prevQueueList) => {
          const updatedQueueList = { ...prevQueueList }
          delete updatedQueueList[ticket.counter_lane as string]
          return updatedQueueList
        })
      }

      if (ticket.trigger === 'skip') {
        setCalledTicket(defaultTicket)
      }

      if (ticket.trigger === 'cancel') {
        // dibatalkan bisa saat dipanggil (popup) maupun saat dilayani (grid now serving)
        if (ticket.queue_no === calledTicket.queue_no) {
          setCalledTicket(defaultTicket)
        }
        setQueueCalledTicket((prevQueue) => prevQueue.filter((item) => item.queue_id !== ticket.queue_id))
        setQueueList((prevQueueList) => {
          if (prevQueueList[ticket.counter_lane as string] !== ticket.queue_no) {
            return prevQueueList
          }
          const updatedQueueList = { ...prevQueueList }
          delete updatedQueueList[ticket.counter_lane as string]
          return updatedQueueList
        })
      }

      debug('info', 'Socket `cs_update` response:', response)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calledTicket, isSound],
  )

  useEffect(() => {
    if (socket) {
      socket.on('cs_update', handleCsUpdate)

      return () => {
        socket.off('cs_update')
      }
    }
  }, [socket, handleCsUpdate])

  useEffect(() => {
    if (queueCalledTicket.length > 0 && !isCalledTicketRunning) {
      setIsCalledTicketRunning(true)
      setTimeout(() => {
        setIsCalledTicketRunning(false)
      }, 10000)

      const nextTicket = queueCalledTicket[0]
      setCalledTicket(nextTicket)
      if (isSound && nextTicket.queue_no && nextTicket.counter_lane) {
        playQueueSound(nextTicket.queue_no, nextTicket.counter_lane)
          .then(() => debug('info', 'Queue sound played'))
          .catch(() => debug('error', 'Queue sound error'))
      }
      setQueueCalledTicket((prevQueue) => prevQueue.slice(1))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueCalledTicket, isCalledTicketRunning, isSound, playQueueSound])

  return (
    <PreviewContext.Provider
      value={{
        isPreviewStarted,
        isLoading,
        queueList,
        startPreview,
        calledTicket,
        locationName,
      }}
    >
      {children}
    </PreviewContext.Provider>
  )
}

export { PreviewContext, PreviewProvider }
